/**
 * src/booking/PayPalReturn.jsx
 *
 * This page handles the redirect back from PayPal after the user approves payment.
 * PayPal redirects to: /paypal-success?token=ORDER_ID&PayerID=XXXXX
 *
 * Add to your App.js routes:
 *   <Route path="/paypal-success" element={<PayPalReturn />} />
 *   <Route path="/paypal-cancel"  element={<PayPalCancel />} />
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'https://carrental.oriesolutions.co.in/api';

export default function PayPalReturn() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token   = params.get('token');    // PayPal order ID
    const payerId = params.get('PayerID');  // PayPal payer ID

    if (!token || !payerId) {
      setStatus('error');
      setErrorMsg('Missing PayPal parameters. Please try again.');
      return;
    }

    // Restore booking state from sessionStorage
    const saved = sessionStorage.getItem('paypal_booking');
    if (!saved) {
      setStatus('error');
      setErrorMsg('Booking session expired. Please start over.');
      return;
    }

    const { ctx, personalInfo, bookingRef, paidAmount, grandTotal } = JSON.parse(saved);

    // Capture the PayPal payment
    fetch(`${API_BASE}/paypal/capture-order`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ orderId: token }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          sessionStorage.removeItem('paypal_booking');
          setStatus('success');

          // Navigate to confirmation
          navigate('/booking-step5', {
            state: {
              ...ctx,
              personalInfo,
              paymentMethod: 'paypal',
              paymentStatus: 'success',
              bookingRef,
              paymentId:   data.paymentId,
              paidAmount,
              grandTotal,
            },
          });
        } else {
          setStatus('error');
          setErrorMsg(data.error || 'Payment capture failed.');
        }
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.message);
      });
  }, []);

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#09090E', fontFamily: 'DM Sans, sans-serif', color: '#E4E4F0',
      flexDirection: 'column', gap: '1rem', padding: '2rem',
    }}>
      {status === 'processing' && (
        <>
          <div style={{ width: 48, height: 48, border: '3px solid rgba(201,168,76,.2)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin .75s linear infinite' }} />
          <div style={{ color: '#C9A84C', fontSize: '1rem' }}>Confirming your PayPal payment…</div>
          <div style={{ color: '#6B6B8A', fontSize: '.8rem' }}>Please do not close this window.</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      )}
      {status === 'error' && (
        <>
          <div style={{ fontSize: '3rem' }}>❌</div>
          <div style={{ color: '#E24B4A', fontSize: '1.1rem', fontWeight: 700 }}>Payment Failed</div>
          <div style={{ color: '#7a7a9a', fontSize: '.85rem', textAlign: 'center', maxWidth: 400 }}>{errorMsg}</div>
          <button
            onClick={() => navigate(-2)}
            style={{ marginTop: '1rem', background: '#C9A84C', color: '#0A0A0F', border: 'none', borderRadius: 10, padding: '.75rem 2rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </>
      )}
    </div>
  );
}

// ── PayPal Cancel page ────────────────────────────────────────
export function PayPalCancel() {
  const navigate = useNavigate();
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#09090E', fontFamily: 'DM Sans, sans-serif', color: '#E4E4F0',
      flexDirection: 'column', gap: '1rem', padding: '2rem',
    }}>
      <div style={{ fontSize: '3rem' }}>🚫</div>
      <div style={{ color: '#D97706', fontSize: '1.1rem', fontWeight: 700 }}>Payment Cancelled</div>
      <div style={{ color: '#7a7a9a', fontSize: '.85rem' }}>You cancelled the PayPal payment. Your booking was not charged.</div>
      <button
        onClick={() => navigate(-2)}
        style={{ marginTop: '1rem', background: '#C9A84C', color: '#0A0A0F', border: 'none', borderRadius: 10, padding: '.75rem 2rem', fontWeight: 700, cursor: 'pointer' }}
      >
        Go Back & Try Again
      </button>
    </div>
  );
}
